'use client';

import type { GlobalFilterItem, BaseHistogramGlobalFilterItem } from '@weberus/shared-types';
import { useEffect, useState } from 'react';
import { hasItems } from '@/app/_shared/utils/has-items';
import { cn } from '@/app/_shared/utils/tw-cn';
import NumberInputBox from './number-input-box';
import { getTranslateYPercentOfBar } from './histogram.utils';
import useHistogramBarBetweenHandle from './hooks/use-histogram-bar-between-handle';
import useHistogramDragHandler from './hooks/use-histogram-drag-handler';

interface HistogramProps extends BaseHistogramGlobalFilterItem {
  type: 'HISTOGRAM_NUMBER_RANGE' | 'FLAT_NUMBER_RANGE_WITHOUT_UPPER_LIMIT';
  /** 히스토그램의 범위가 *최종* 변경될 때 호출되는 콜백 */
  onRangeChange: (min: number, max: number) => void;
  onClickHistogramHandle: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  onFocusHistogramInput: (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => void;
  /** 히스토그램이 선택되었는지 여부 */
  isSelected: boolean;
}

/**
 * [AC] 가격 필터 정책
 * @see {@link https://yanolja.atlassian.net/wiki/spaces/PLPSRP/pages/246417352/AC/pages/41034774} - AC문서
 */
const Histogram: React.FC<HistogramProps> = ({
  onRangeChange,
  onClickHistogramHandle,
  onFocusHistogramInput,
  isSelected,
  ...item
}) => {
  const {
    histogramRef,

    draggingHandle,
    handlePosition,
    zIndexLeft,
    zIndexRight,

    leftHandlePosition,
    rightHandlePosition,
    onHandleDragStart,
    handleMinInputBlur,
    handleMaxInputBlur,
    handleOnFocusHistogramInput,

    minInputValue,
    setMinInputValue,
    maxInputValue,
    setMaxInputValue,
  } = useHistogramDragHandler({
    ...item,
    isSelected,
    onRangeChange,
    onClickHistogramHandle,
    onFocusHistogramInput,
  });

  const {
    histogramBarRefs,
    leftHandleRef,
    rightHandleRef,

    isHistogramBarBetweenHandle,
  } = useHistogramBarBetweenHandle({
    leftHandlePosition,
    rightHandlePosition,
  });
  const [isFocused, setIsFocused] = useState(false);
  const [isOverflowIndicator, setIsOverflowIndicator] = useState(false);
  const { bins, countRange } = item;

  useEffect(() => {
    // 해외숙소 최대가격 범위 초과시 '+' 기호 노출
    if (isFocused) {
      setIsOverflowIndicator(false);
    } else {
      setIsOverflowIndicator(Number(maxInputValue?.replaceAll(',', '')) >= 1_000_000);
    }
  }, [maxInputValue]);

  return (
    <>
      <div className="relative mb-20 pb-14">
        <div className={cn('relative h-74 overflow-hidden', { 'h-20': !hasItems(bins) })}>
          {!!countRange && hasItems(bins) && (
            <div className="mx-16 grid grid-flow-col gap-x-4">
              {bins.map((bin, idx) => (
                <div
                  ref={(el) => el && (histogramBarRefs.current[idx] = el)}
                  key={idx}
                  className={cn(
                    'h-70 overflow-hidden rounded-tl-4 rounded-tr-4 bg-fill-neutral-main',
                    !isHistogramBarBetweenHandle(idx) && 'bg-fill-neutral-weak2',
                  )}
                  style={{
                    transform: `translateY(${getTranslateYPercentOfBar(bin.count, countRange.max, countRange.min)}%)`,
                  }}
                />
              ))}
            </div>
          )}
          <div className="absolute bottom-0 h-4 w-full flex-shrink-0 rounded-[2px] bg-fill-neutral-weak2" />
          <div className="absolute bottom-0 mx-16 w-[calc(100%-32px)]">
            <div
              className="relative h-4 w-full flex-shrink-0 rounded-[2px] bg-fill-neutral-main"
              style={{
                left: `${handlePosition.min}%`,
                width: `${handlePosition.max - handlePosition.min}%`,
              }}
            />
          </div>
        </div>
        <div ref={histogramRef} className="relative w-[calc(100%-32px)] translate-y-[14px]">
          <div
            ref={leftHandleRef}
            style={{ left: `${leftHandlePosition}%`, zIndex: zIndexLeft }}
            className={cn(
              'absolute bottom-0 left-0 size-32 cursor-pointer rounded-full border border-line-neutral-weak3 bg-static-white shadow-2',
              draggingHandle === 'LEFT' && 'border-line-neutral-medium',
            )}
            onMouseDown={onHandleDragStart('LEFT')}
            onTouchStart={onHandleDragStart('LEFT')}
          />
          <div
            ref={rightHandleRef}
            style={{ left: `${rightHandlePosition}%`, zIndex: zIndexRight }}
            className={cn(
              'absolute bottom-0 left-0 size-32 cursor-pointer rounded-full border border-line-neutral-weak3 bg-static-white shadow-2',
              draggingHandle === 'RIGHT' && 'border-line-neutral-medium',
            )}
            onMouseDown={onHandleDragStart('RIGHT')}
            onTouchStart={onHandleDragStart('RIGHT')}
          />
        </div>
      </div>
      <div className="flex items-center">
        <NumberInputBox
          id="minValueInput"
          label="최소"
          value={minInputValue}
          ariaLabel="최솟값 입력 필드"
          onChange={setMinInputValue}
          onBlur={handleMinInputBlur}
          onFocus={() => {
            handleOnFocusHistogramInput('min');
          }}
        />
        <div className="mx-4 h-1 w-12 flex-shrink-0 bg-fill-neutral-weak3" />
        {item.type === 'FLAT_NUMBER_RANGE_WITHOUT_UPPER_LIMIT' ? (
          <NumberInputBox
            id="maxValueInput"
            label="최대"
            value={`${maxInputValue}${isOverflowIndicator ? '+' : ''}`}
            ariaLabel="최댓값 입력 필드"
            onChange={setMaxInputValue}
            onBlur={(value: number) => {
              handleMaxInputBlur(value);
              setIsFocused(false);
              setIsOverflowIndicator(value >= 1_000_000);
            }}
            onFocus={() => {
              handleOnFocusHistogramInput('max');
              setIsFocused(true);
              setIsOverflowIndicator(false);
            }}
          />
        ) : (
          <NumberInputBox
            id="maxValueInput"
            label="최대"
            value={maxInputValue}
            ariaLabel="최댓값 입력 필드"
            onChange={setMaxInputValue}
            onBlur={handleMaxInputBlur}
            onFocus={() => {
              handleOnFocusHistogramInput('max');
            }}
          />
        )}
      </div>
    </>
  );
};

export default Histogram;
